import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import type {
  EnquiryItem,
  ItineraryDraftItem,
  LeadContactOption,
  PaginatedResponse,
} from "../../features/admin/types";
import Pagination from "../../components/admin/Pagination";
import RefreshButton from "../../components/admin/RefreshButton";
import Spinner from "../../components/ui/Spinner";
import LeadStatusGuide, { LeadStatusPill } from "../../components/admin/LeadStatusGuide";
import { LEAD_STATUS_ORDER, describeLeadStatus } from "../../features/admin/leadStatusGuide";
import SessionConversationDrawer from "../../components/admin/SessionConversationDrawer";

const STATUS_OPTIONS = ["", ...LEAD_STATUS_ORDER];

/** Which enquiries get a "View chat" action: a buyer-seller chat exists once the
 *  enquiry is linked to a DM thread (`extra.thread_id` — set on accept and on
 *  Get Details AI threads) or the seller accepted / replied (accepted, contacted;
 *  a broadcast lead's per-seller threads are found server-side). Open enquiries
 *  and ones closed before any accept never had a chat, so the action is simply
 *  not rendered for them ("—"). */
function canViewEnquiryChat(e: EnquiryItem): boolean {
  return Boolean(e.extra?.thread_id) || e.status === "accepted" || e.status === "contacted";
}

const ViewChatButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={(ev) => {
      ev.stopPropagation();
      onClick();
    }}
    className="px-2 py-1 rounded-md bg-gray-800 text-indigo-300 hover:text-white hover:bg-gray-700 text-[11px] font-medium whitespace-nowrap transition-colors"
  >
    View chat
  </button>
);

/** Read-only buyer-seller chat for one enquiry (AI auto-replies marked), in the shared conversation drawer. */
const EnquiryChatDrawer: React.FC<{ enquiry: EnquiryItem; onClose: () => void }> = ({ enquiry, onClose }) => (
  <SessionConversationDrawer
    key={enquiry.id}
    userId={enquiry.buyer_contact?.id || ""}
    sessionId={enquiry.id}
    title={`Enquiry chat: ${enquiry.buyer_contact?.name || "Buyer"} ↔ ${enquiry.seller_contact?.name || "Seller"}`}
    fetchPage={(p) => adminService.getEnquiryConversation(enquiry.id, p)}
    onClose={onClose}
  />
);

const ContactCell: React.FC<{
  contact: EnquiryItem["buyer_contact"];
  fallbackLabel: string;
  onReactivated?: () => void;
}> = ({ contact, fallbackLabel, onReactivated }) => {
  const [reactivating, setReactivating] = useState(false);

  if (!contact || (!contact.name && !contact.email && !contact.phone)) {
    // `account_missing`/`account_status: "deleted"` with no name means this
    // WAS a routed lead (a real dealer_id was resolved at send time) whose
    // seller account and the deal it was attached to have both since been
    // deleted — nothing left to recover a name from. Distinct from a lead
    // that was genuinely never routed to anyone (fallbackLabel below).
    if (contact?.account_missing || contact?.account_status === "deleted") {
      return <span className="text-gray-600 italic">Seller account deleted</span>;
    }
    return <span className="text-gray-600 italic">{fallbackLabel}</span>;
  }

  const handleReactivate = async () => {
    if (!contact.id || reactivating) return;
    setReactivating(true);
    try {
      await adminService.updateUser(contact.id, { is_active: true, restricted: false });
      onReactivated?.();
    } catch (err) {
      console.error(err);
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div className="space-y-0.5">
      <div className="text-white font-medium">
        {contact.name || "—"}
        {contact.account_missing && (
          <span className="ml-1.5 text-[10px] font-normal text-amber-500 italic">
            (deleted account)
          </span>
        )}
        {contact.account_status === "restricted" && (
          <span className="ml-1.5 text-[10px] font-normal text-red-400 italic">
            (inactive — restricted)
          </span>
        )}
      </div>
      {contact.business_name && (
        <div className="text-xs text-gray-500">{contact.business_name}</div>
      )}
      {contact.phone && <div className="text-xs text-gray-400">📞 {contact.phone}</div>}
      {contact.whatsapp_number && (
        <div className="text-xs text-gray-400">💬 {contact.whatsapp_number}</div>
      )}
      {contact.email && <div className="text-xs text-gray-400">{contact.email}</div>}
      {contact.account_status === "restricted" && (
        <button
          onClick={handleReactivate}
          disabled={reactivating}
          className="mt-1 text-[11px] font-medium text-emerald-400 hover:text-emerald-300 disabled:opacity-50"
        >
          {reactivating ? "Reactivating…" : "Make account active"}
        </button>
      )}
    </div>
  );
};

/** AI itinerary drafts — never sent to any seller (see
 *  ITINERARY_LEAD_FLOW_REPORT_2026-09.md). Its own tab so it never mixes
 *  with real enquiries, and "Sent To" here means "deals in this draft
 *  happen to belong to", not "was sent to" — read-only, resolved at
 *  display time, never written back onto the lead. */
const ItineraryDraftsTab: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse<ItineraryDraftItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        (await adminService.getItineraryDrafts({ page, page_size: pageSize })) as PaginatedResponse<ItineraryDraftItem>
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  return (
    <div>
      <AdminPageHeader
        title="Itinerary Drafts"
        subtitle="AI-generated itineraries saved from Ask Gullivr chat. These were never sent to any seller — the buyer has to explicitly click Send Enquiry/Connect on a specific deal for that."
        meta={`${data?.total ?? 0} total`}
        actions={<RefreshButton onRefresh={fetchDrafts} />}
      />

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Buyer</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Deals touch these seller(s)</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Itinerary</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Destination</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-20 text-center text-gray-500">
                    <Spinner />
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-20 text-center text-gray-500">
                    No itinerary drafts found
                  </td>
                </tr>
              ) : (
                data?.items.map((d) => {
                  const text = d.enquiry_text || "";
                  const isOpen = expanded === d.id;
                  return (
                    <tr
                      key={d.id}
                      className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors align-top"
                    >
                      <td className="py-3 px-4">
                        <ContactCell contact={d.buyer_contact} fallbackLabel="Unknown buyer" />
                      </td>
                      <td className="py-3 px-4">
                        {d.touched_sellers.length === 0 ? (
                          <span className="text-gray-600 italic">No resolvable seller (deals deleted/reindexed)</span>
                        ) : (
                          <div className="space-y-1">
                            {d.touched_sellers.map((s) => (
                              <div key={s.id} className="text-gray-300">
                                {s.name || "(unnamed seller)"}
                                {s.deal_title && (
                                  <div className="text-xs text-gray-500">{s.deal_title}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 max-w-sm">
                        <p className={`text-gray-300 whitespace-pre-wrap ${isOpen ? "" : "line-clamp-3"}`}>
                          {text || <span className="text-gray-600 italic">No itinerary text</span>}
                        </p>
                        {text.length > 140 && (
                          <button
                            onClick={() => setExpanded(isOpen ? null : d.id)}
                            className="text-xs text-indigo-400 hover:text-indigo-300 mt-1"
                          >
                            {isOpen ? "Show less" : "Show more"}
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-400">
                        {Array.isArray(d.destination) && d.destination.length > 0
                          ? d.destination.join(", ")
                          : "—"}
                      </td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">{d.created_at || "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={data.total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
};

/** "Get Details" AI chat trace — sender, the deal/DMC card that opened the
 *  chat, the raw AI back-and-forth, any errors swallowed along the way, and
 *  the final summary that was actually sent to the seller. Every field here
 *  comes straight through `extra` on the lead (see `dm/routes.py`'s
 *  `deal_ai_chat_finalize`) — nothing new to fetch, just a focused view. */
const AiReplyChatsTab: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse<EnquiryItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [chatFor, setChatFor] = useState<EnquiryItem | null>(null);

  const fetchChats = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        (await adminService.getLeads({
          page,
          page_size: pageSize,
          lead_types: "get_details_ai_chat,get_details_ai_enquiry",
        })) as PaginatedResponse<EnquiryItem>
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  return (
    <div>
      <AdminPageHeader
        title="AI Reply Chats"
        subtitle="Every 'Get Details' AI chat — who opened it, which deal/DMC card, the full back-and-forth, any errors, and the summary that was actually sent to the seller."
        meta={`${data?.total ?? 0} total`}
        actions={<RefreshButton onRefresh={fetchChats} />}
      />

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Sender</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Deal / DMC card</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Outcome</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Summary sent</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-20 text-center text-gray-500">
                    <Spinner />
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-20 text-center text-gray-500">
                    No AI reply chats found
                  </td>
                </tr>
              ) : (
                data?.items.map((e) => {
                  const isOpen = expanded === e.id;
                  const extra = e.extra || {};
                  const transcript: string = extra.raw_transcript || "";
                  const errors: string[] = extra.errors || [];
                  const dealTitle: string = extra.deal_title || "";
                  const threadId: string = extra.thread_id || "";
                  const summary = e.enquiry_text || "";
                  return (
                    <>
                      <tr
                        key={e.id}
                        className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors align-top cursor-pointer"
                        onClick={() => setExpanded(isOpen ? null : e.id)}
                      >
                        <td className="py-3 px-4">
                          <ContactCell contact={e.buyer_contact} fallbackLabel="Unknown buyer" />
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          {dealTitle || <span className="text-gray-600 italic">Direct enquiry to seller (no specific deal)</span>}
                          <div className="text-xs text-gray-500 mt-0.5">
                            Seller: <ContactCell contact={e.seller_contact} fallbackLabel="Unrouted / broadcast" />
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {threadId ? (
                            <span
                              className="px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-500/10 text-indigo-400"
                              title="Seller's AI auto-reply was ON — buyer went straight into a live chat thread."
                            >
                              💬 Live thread opened
                            </span>
                          ) : (
                            <span
                              className="px-2 py-0.5 rounded-md text-xs font-medium bg-amber-500/10 text-amber-400"
                              title="Became a pending enquiry — the seller must Accept before any chat starts."
                            >
                              ⏳ Pending enquiry ({e.status || "open"})
                            </span>
                          )}
                          {canViewEnquiryChat(e) && (
                            <div className="mt-1">
                              <ViewChatButton onClick={() => setChatFor(e)} />
                            </div>
                          )}
                          {errors.length > 0 && (
                            <div
                              className="mt-1 px-2 py-0.5 rounded-md text-xs font-medium bg-rose-500/10 text-rose-400 inline-block"
                              title={errors.join(" | ")}
                            >
                              ⚠ {errors.length} error{errors.length > 1 ? "s" : ""}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 max-w-sm">
                          <p className={`text-gray-300 whitespace-pre-wrap ${isOpen ? "" : "line-clamp-2"}`}>
                            {summary || <span className="text-gray-600 italic">No summary</span>}
                          </p>
                        </td>
                        <td className="py-3 px-4 text-gray-400 whitespace-nowrap">{e.created_at || "—"}</td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-gray-800/50 bg-gray-950/50">
                          <td colSpan={5} className="py-4 px-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div>
                                <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                                  Raw AI chat transcript
                                </p>
                                <pre className="whitespace-pre-wrap text-xs text-gray-300 bg-black/30 rounded-lg p-3 max-h-72 overflow-y-auto">
                                  {transcript || "No transcript recorded (sent before this tracing was added)."}
                                </pre>
                              </div>
                              <div>
                                {errors.length > 0 && (
                                  <>
                                    <p className="text-xs font-semibold text-rose-400 mb-1.5 uppercase tracking-wide">
                                      Errors during this enquiry
                                    </p>
                                    <ul className="list-disc list-inside text-xs text-rose-300 bg-rose-500/5 rounded-lg p-3 mb-3 space-y-1">
                                      {errors.map((err, i) => (
                                        <li key={i}>{err}</li>
                                      ))}
                                    </ul>
                                  </>
                                )}
                                <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                                  Full summary sent to seller
                                </p>
                                <pre className="whitespace-pre-wrap text-xs text-gray-300 bg-black/30 rounded-lg p-3 max-h-72 overflow-y-auto">
                                  {summary || "—"}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={data.total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
        />
      )}
      {chatFor && <EnquiryChatDrawer enquiry={chatFor} onClose={() => setChatFor(null)} />}
    </div>
  );
};

const AdminEnquiriesPage: React.FC = () => {
  const [tab, setTab] = useState<"enquiries" | "itinerary_drafts" | "ai_reply_chats">("enquiries");
  const [data, setData] = useState<PaginatedResponse<EnquiryItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  // Exact-match ids from the real present list (see buyerOptions/sellerOptions
  // below) — precise even when two people share a name, and their combined
  // name/email/phone label doubles as an email/phone filter.
  const [buyerId, setBuyerId] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [chatFor, setChatFor] = useState<EnquiryItem | null>(null);
  // Only actually-filled company/brand/agency names — not every seller has
  // one, so this list (not free text) is what "group by company" filters on.
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  // Distinct buyers/sellers who actually appear in `leads` — not every user
  // in the system — so the dropdown only ever offers real senders/recipients.
  const [buyerOptions, setBuyerOptions] = useState<LeadContactOption[]>([]);
  const [sellerOptions, setSellerOptions] = useState<LeadContactOption[]>([]);

  useEffect(() => {
    adminService.getCompanyNames().then(setCompanyNames).catch(() => {});
    adminService.getLeadBuyerOptions().then(setBuyerOptions).catch(() => {});
    adminService.getLeadSellerOptions().then(setSellerOptions).catch(() => {});
  }, []);

  const contactOptionLabel = (o: LeadContactOption) =>
    [o.name || "(no name)", o.email, o.phone || o.whatsapp_number].filter(Boolean).join(" — ");

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search) params.search = search;
      if (status) params.lead_status = status;
      if (buyerId) params.buyer_id = buyerId;
      if (sellerId) params.seller_id = sellerId;
      else if (sellerName) params.seller_name = sellerName;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      setData((await adminService.getLeads(params)) as PaginatedResponse<EnquiryItem>);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, status, buyerId, sellerId, sellerName, dateFrom, dateTo]);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  useEffect(() => {
    setPage(1);
  }, [pageSize, status, buyerId, sellerId, sellerName, dateFrom, dateTo]);

  return (
    <div>
      <div className="mb-4 flex gap-2 border-b border-gray-800">
        <button
          onClick={() => setTab("enquiries")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "enquiries"
              ? "border-indigo-500 text-white"
              : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          Enquiries
        </button>
        <button
          onClick={() => setTab("itinerary_drafts")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "itinerary_drafts"
              ? "border-indigo-500 text-white"
              : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          Itinerary Drafts
        </button>
        <button
          onClick={() => setTab("ai_reply_chats")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === "ai_reply_chats"
              ? "border-indigo-500 text-white"
              : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          AI Reply Chats
        </button>
      </div>

      {tab === "itinerary_drafts" ? (
        <ItineraryDraftsTab />
      ) : tab === "ai_reply_chats" ? (
        <AiReplyChatsTab />
      ) : (
        <>
      <AdminPageHeader
        title="Enquiries"
        subtitle="Every buyer enquiry — who sent it, which seller received it, and both parties' contact details."
        meta={`${data?.total ?? 0} total`}
        actions={<RefreshButton onRefresh={fetchEnquiries} />}
      />

      <div className="mb-6 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Search</label>
          <input
            type="text"
            placeholder="Text, category, destination..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 w-64"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Buyer name</label>
          <select
            value={buyerId}
            onChange={(e) => setBuyerId(e.target.value)}
            className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 w-64"
          >
            <option value="">All buyers</option>
            {buyerOptions.map((o) => (
              <option key={o.id} value={o.id}>{contactOptionLabel(o)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Seller name</label>
          <select
            value={sellerId}
            onChange={(e) => setSellerId(e.target.value)}
            className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 w-64"
          >
            <option value="">All sellers</option>
            {sellerOptions.map((o) => (
              <option key={o.id} value={o.id}>{contactOptionLabel(o)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Company / Brand / Agency</label>
          <select
            value={sellerName && companyNames.includes(sellerName) ? sellerName : ""}
            onChange={(e) => setSellerName(e.target.value)}
            className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 w-56"
          >
            <option value="">All companies</option>
            {companyNames.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">From date</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To date</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s} title={s ? describeLeadStatus(s).tooltip : undefined}>
                {s ? describeLeadStatus(s).label : "All statuses"}
              </option>
            ))}
          </select>
        </div>
        {(search || buyerId || sellerId || sellerName || dateFrom || dateTo || status) && (
          <button
            onClick={() => {
              setSearch("");
              setBuyerId("");
              setSellerId("");
              setSellerName("");
              setDateFrom("");
              setDateTo("");
              setStatus("");
            }}
            className="px-3 py-2.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      <LeadStatusGuide />

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Sent By (Buyer)</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Sent To (Seller)</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Reason</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Enquiry</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Destination</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium" title="Newest update first">Updated ↓</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Chat</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-20 text-center text-gray-500">
                    <Spinner />
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-20 text-center text-gray-500">
                    No enquiries found
                  </td>
                </tr>
              ) : (
                data?.items.map((e) => {
                  const text = e.enquiry_text || e.user_message || "";
                  const isOpen = expanded === e.id;
                  return (
                    <tr
                      key={e.id}
                      className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors align-top"
                    >
                      <td className="py-3 px-4">
                        <ContactCell contact={e.buyer_contact} fallbackLabel="Unknown buyer" />
                      </td>
                      <td className="py-3 px-4">
                        <ContactCell
                          contact={e.seller_contact}
                          fallbackLabel="Unrouted / broadcast"
                          onReactivated={fetchEnquiries}
                        />
                      </td>
                      <td className="py-3 px-4 max-w-xs text-xs text-gray-500">
                        {e.seller_resolution_reason || "—"}
                        {/* Live deal state, computed per request by the leads
                            list endpoint — not a stored field on the lead. */}
                        {e.deal_inactive && (
                          <div className="mt-1 inline-block rounded bg-amber-500/10 px-2 py-0.5 text-xs text-amber-400">
                            Deal expired/unpublished — this enquiry has no seller-side action available now.
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 max-w-sm">
                        <p className={`text-gray-300 whitespace-pre-wrap ${isOpen ? "" : "line-clamp-3"}`}>
                          {text || <span className="text-gray-600 italic">No message</span>}
                        </p>
                        {text.length > 140 && (
                          <button
                            onClick={() => setExpanded(isOpen ? null : e.id)}
                            className="text-xs text-indigo-400 hover:text-indigo-300 mt-1"
                          >
                            {isOpen ? "Show less" : "Show more"}
                          </button>
                        )}
                        <div className="text-xs text-gray-600 mt-1">{e.lead_type}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-400">
                        {Array.isArray(e.destination) && e.destination.length > 0
                          ? e.destination.join(", ")
                          : "—"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap items-center gap-1">
                          <LeadStatusPill status={e.status} />
                          {e.extra?.auto_accepted && (
                            <span
                              className="px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-500/10 text-indigo-400"
                              title="Auto-accepted — the seller's AI auto-reply was ON for this deal, so the buyer was dropped straight into a live thread instead of waiting for the seller to click Accept."
                            >
                              🤖 Auto (AI reply was ON)
                            </span>
                          )}
                          {/* Get Details' confirm dialog ("Send to seller?") only exists on
                              get_details_ai_chat / get_details_ai_enquiry leads — every other
                              lead_type never went through a Yes/No step, so no tag is shown. */}
                          {e.lead_type === "get_details_ai_chat" && (
                            <span
                              className="px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-500/10 text-emerald-400"
                              title="Buyer clicked Yes on the 'Send to seller?' confirm dialog and was routed into a live thread (seller's AI auto-reply was ON)."
                            >
                              ✅ Yes
                            </span>
                          )}
                          {e.lead_type === "get_details_ai_enquiry" && (
                            e.extra?.silent ? (
                              <span
                                className="px-2 py-0.5 rounded-md text-xs font-medium bg-rose-500/10 text-rose-400"
                                title="Buyer clicked No / closed the 'Send to seller?' dialog. Still recorded here for tracking and visible to the seller, but the buyer was never notified and it's hidden from their own Sent list."
                              >
                                ❌ No
                              </span>
                            ) : (
                              <span
                                className="px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-500/10 text-emerald-400"
                                title="Buyer clicked Yes on the 'Send to seller?' confirm dialog."
                              >
                                ✅ Yes
                              </span>
                            )
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-300 whitespace-nowrap" data-testid="enquiry-updated-at">
                        {/* Same pre-formatted IST string as the Date column; the rows come from
                            the server already sorted by it (newest first) — not re-sorted here. */}
                        {e.updated_at || e.created_at || "—"}
                      </td>
                      <td className="py-3 px-4 text-gray-400 whitespace-nowrap">
                        {/* created_at arrives pre-formatted in IST ("04 May 2026, 06:30 PM IST")
                            by the backend's global response datetime rewrite — re-parsing it
                            with `new Date()` here produces "Invalid Date", so render as-is. */}
                        {e.created_at || "—"}
                      </td>
                      <td className="py-3 px-4" data-testid="enquiry-chat-cell">
                        {canViewEnquiryChat(e) ? (
                          <ViewChatButton onClick={() => setChatFor(e)} />
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={data.total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
        />
      )}
      {chatFor && <EnquiryChatDrawer enquiry={chatFor} onClose={() => setChatFor(null)} />}
        </>
      )}
    </div>
  );
};

export default AdminEnquiriesPage;
